import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useLayoutEffect,
} from 'react';
import styles from './QRGenerator.module.scss';
import { QRCodeCanvas } from 'qrcode.react';

export interface QRGeneratorProps {
  value: string;
  size?: number;
  fgColor?: string;
  bgColor?: string;
  showDownload?: boolean;
  className?: string;
  filename?: string;
  /** Ask parent to clear the preview (parent should set value="") */
  onClear?: () => void;
  /** Automatically clear preview after download */
  clearOnDownload?: boolean;
}

export interface QRGeneratorHandle {
  getCanvas: () => HTMLCanvasElement | null;
  clear: () => void;
}

const QRGenerator = forwardRef<QRGeneratorHandle, QRGeneratorProps>(
  (
    {
      value,
      size = 180,
      fgColor = '#000000',
      bgColor = '#ffffff',
      showDownload = true,
      className = '',
      filename = 'qr-code.png',
      onClear,
      clearOnDownload = true,
    },
    ref
  ) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    // Capture the inner <canvas> rendered by qrcode.react
    useLayoutEffect(() => {
      canvasRef.current =
        wrapperRef.current?.querySelector<HTMLCanvasElement>('canvas') ?? null;
    }, [value, size, fgColor, bgColor]);

    const requestClear = useCallback(() => {
      onClear?.();
    }, [onClear]);

    useImperativeHandle(ref, () => ({
      getCanvas: () => canvasRef.current,
      clear: requestClear,
    }));

    const handleDownload = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const maybeClear = () => {
        if (clearOnDownload) {
          // Let the download click finish first
          setTimeout(() => requestClear(), 0);
        }
      };

      if (canvas.toBlob) {
        canvas.toBlob((blob) => {
          if (!blob) return;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
          maybeClear();
        }, 'image/png');
      } else {
        const url = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        maybeClear();
      }
    }, [filename, clearOnDownload, requestClear]);

    // No value -> show placeholder box instead of a QR
    if (!value) {
      return (
        <div className={styles.qrPlaceholder} aria-live="polite">
          QR Code
        </div>
      );
    }

    return (
      <div className={`${styles.qrBox} ${className}`} ref={wrapperRef}>
        <QRCodeCanvas
          value={value}
          size={size}
          fgColor={fgColor}
          bgColor={bgColor}
          includeMargin
          level="M"
          role="img"
          aria-label="QR code"
        />
        {showDownload && (
          <button
            type="button"
            className={styles.qrDownloadBtn}
            onClick={handleDownload}
            title="Télécharger le QR Code"
          >
            Télécharger le QR Code
          </button>
        )}
      </div>
    );
  }
);

QRGenerator.displayName = 'QRGenerator';
export default QRGenerator;
